/**
 * Labstep
 *
 * @module grid/Index/coldefs/Resource/metadatas/entityRelation
 * @desc ColDef for Metadata of type EntityRelation
 */

import { EntityLink } from 'labstep-web/components/Entity/Link';
import { EntityRelationSearchSelect } from 'labstep-web/components/EntityRelation/SearchSelect';
import { ICONS } from 'labstep-web/constants/icons';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { EntityRelation } from 'labstep-web/models/entity-relation.model';
import type { Metadata } from 'labstep-web/models/metadata';
import type { colDefMetadataEntityRelationType } from './types';

export const colDefMetadataEntityRelation: colDefMetadataEntityRelationType =
  (metadataTemplate, entityName) => {
    return {
      colId: `metadata:${metadataTemplate.guid}`,
      headerName: metadataTemplate.field_definition!.name,
      headerComponentParams: {
        icon: ICONS.metadata.secondary,
        cornerIcon: ICONS[entityName].primary,
      },
      valueGetter: (params) => {
        // Find the concrete metadata instance for this resource row matching the template field definition
        const fieldDefId = metadataTemplate.field_definition?.id;
        if (!fieldDefId) {
          return '';
        }
        const metadatas: Metadata[] = params.data?.metadatas || [];
        const metadataInstance = metadatas.find(
          (m) =>
            m.field_definition &&
            m.field_definition.id === fieldDefId,
        );
        if (!metadataInstance) {
          return '';
        }
        const preview: EntityRelation[] =
          (metadataInstance as any).entity_relation_preview || [];

        return preview
          .filter((er) => er.target_resource || er.source_resource)
          .map((er) => {
            // Determine the counterpart entity relative to this resource row
            const resourceId = params.data?.id;
            const isSource =
              er.source_resource &&
              er.source_resource.id === resourceId;
            const counterpart = isSource
              ? er.target_resource
              : er.source_resource;
            return counterpart?.name;
          })
          .filter(Boolean);
      },
      cellEditorPopup: true,
      editable: true,
      cellRenderer: (params) => {
        const fieldDefId = metadataTemplate.field_definition?.id;
        if (!fieldDefId) {
          return '';
        }
        const metadatas: Metadata[] = params.data?.metadatas || [];
        const metadataInstance = metadatas.find(
          (m) =>
            m.field_definition &&
            m.field_definition.id === fieldDefId,
        );
        const preview: EntityRelation[] =
          (metadataInstance as any)?.entity_relation_preview || [];
        // If no preview yet fall back to previous behaviour
        const relations: EntityRelation[] = preview;

        if (!relations.length) {
          return <ActionComponent type="text" text="Select" />;
        }
        return (
          <Flex
            spacing="gap"
            hAlignContent="center"
            vAlignContent="center"
            data-cy={`grid/Index/coldefs/Resource/metadatas/entityRelation:CellRenderer-${params.data.name}`}
          >
            {relations.map((er: EntityRelation) => {
              const resourceId = params.data?.id;
              const isSource =
                er.source_resource &&
                er.source_resource.id === resourceId;
              const counterpart = isSource
                ? er.target_resource
                : er.source_resource;
              if (!counterpart) {
                return null;
              }
              return (
                <div key={`${er.guid}-${counterpart.guid}`}>
                  <EntityLink entity={counterpart} showIcon />
                </div>
              );
            })}
          </Flex>
        );
      },
      cellEditorPopupPosition: 'under',
      cellEditor: (params: any) => {
        return (
          <Flex width={'100%'}>
            <EntityCreateContainer
              entityName={EntityRelation.entityName}
            >
              {({ create }): React.ReactElement => (
                <EntityRelationSearchSelect
                  metadata={metadataTemplate}
                  sourceEntity={params.data}
                  createEntityRelation={create}
                  stopEdit={() => params.api.stopEditing()}
                />
              )}
            </EntityCreateContainer>
          </Flex>
        );
      },
    };
  };
